import React from 'react';
import { Link } from 'react-router';
import T from 'prop-types';
// components
import { FormattedMessage, useIntl } from 'react-intl';
import ActivityActionsBar from './ActivityActionsBar/ActivityActionsBar';
import Collapse from 'components/Collapse/Collapse';
import ActivityBox from './ActivityBox';
import ActivityBodyCollapse from './ActivityBodyCollapse';
import ActivityHeader from './ActivityHeader';
import ActivityHeaderBadge from './ActivityHeader/ActivityHeaderBadge';
import ActivityMentions from './ActivityMentions';
import FarmTypeLabel from 'components/FarmTypeLabel';
import FarmFeedRowComments from 'components/FarmFeed/FarmFeedRowComments';
import FarmFeedRowAddComment from 'components/FarmFeed/FarmFeedRowAddComment';
import LastCommentInfo from './LastCommentInfo/LastCommentInfo';
import Divider from 'components/Divider';
import Popover from 'components/Popover';
import TreatmentFarmfeedTooltip from 'components/EntityFarmfeedTooltip/TreatmentFarmfeedTooltip';
import FormattedWeight from 'components/FormattedWeight';
// utils
import classnames from 'classnames/bind';
import moment from 'moment';
import download from 'downloadjs';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
// regional date handling
import { connect } from 'react-redux';
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
import { formatDateForUser } from 'utils/regionalDateHelper';
// endpoints
import { downloadWithdrawalWarningCSV } from 'endpoints/farmfeed';
// hoc
import withActivityController from './hoc/withActivityController';
// styles
import styles from './GeneralWithdrawalWarning.module.scss';

const cn = classnames.bind(styles);

const affectRerenderKeys = ['id', 'updated_at', 'is_viewed', 'flagged', 'comments', 'additional_data'];

const GeneralWithdrawalWarning = ({
  className,
  activity,
  currentUser,
  isChildActivity,
  markAsSeen,
  openDropdown,
  setActivityFlag,
  isMinimized,
  isTruncated,
  toggleMinimized,
  onViewFullActivity,
  defaultDropdownOptions,
  countryCode,
}) => {
  const intl = useIntl();
  const { id, events, created_at, flagged, is_viewed, mentions } = activity;
  const isAdmin = currentUser.roles_map.admin;

  const isUserHasRoles = (user, farm) => {
    if (!user.roles_map) return false;
    const { caretaker, manager, owner, vet, admin, super_admin, tenant_owner } = user.roles_map;
    if (admin || super_admin || tenant_owner) return true;

    const farmId = farm?.id;
    const companyIdOfFarm = farm?.manager?.id || farm?.company_id || null;

    if (farmId) {
      const isManager =
        manager.farm_ids.includes(farmId) || (companyIdOfFarm ? manager.company_ids.includes(companyIdOfFarm) : false);
      const isOwner =
        owner.farm_ids.includes(farmId) || (companyIdOfFarm ? owner.company_ids.includes(companyIdOfFarm) : false);
      const isVet =
        vet.farm_ids.includes(farmId) || (companyIdOfFarm ? vet.company_ids.includes(companyIdOfFarm) : false);
      const isCaregiver =
        caretaker.farm_ids.includes(farmId) ||
        (companyIdOfFarm ? caretaker.company_ids.includes(companyIdOfFarm) : false);

      return isCaregiver || isManager || isOwner || isVet;
    }

    const isManager = !!manager.farm_ids.length || !!manager.company_ids.length;
    const isOwner = !!owner.farm_ids.length || !!owner.company_ids.length;
    const isVet = !!vet.farm_ids.length || !!vet.company_ids.length;
    const isCaregiver = !!caretaker.farm_ids.length || !!caretaker.company_ids.length;

    return isCaregiver || isManager || isOwner || isVet;
  };

  const renderClearanceDate = (date, intl) => {
    if (!date) return intl.formatMessage({ id: 'general.notAvail' });
    const dateObj = moment(date);
    return dateObj.isBefore(moment(), 'day')
      ? intl.formatMessage({ id: 'general.clear' })
      : formatDateForUser(dateObj, countryCode);
  };

  const renderThresholdDate = (date, intl) => {
    if (!date) return intl.formatMessage({ id: 'general.notAvail' });
    return formatDateForUser(date, countryCode);
  };

  const renderMedicationName = (treatment) => {
    // Show treatment name with administration date in parentheses using regional format
    const administeredDate = treatment.administered_on;
    const dateStr = administeredDate ? formatDateForUser(administeredDate, countryCode) : '';
    return treatment.name + (dateStr ? ` (${dateStr})` : '');
  };


  const renderFarmTitle = (farm) => {
    return <div>{farm.name}<FarmTypeLabel className="ml-5" farmType={farm.farm_type} /></div>;
  };

  const handleOpenDropdown = (e) => {
    const handleDownloadCSV = () => {
      const filename = 'EveryPig_Withdrawal_Warning_' + activity.id + '_' + moment().format('MM-DD-YYYY') + '.csv';
      return downloadWithdrawalWarningCSV(activity.id)
        .then((blob) => download(blob, filename, 'text/csv'))
        .catch(toastResponseErrors);
    };

    const options = [
      ...defaultDropdownOptions,
      {
        label: <FormattedMessage id="general.button.exportWithdrawalWarning" />,
        onClick: handleDownloadCSV,
        icon: 'merge'
      },
    ];
    openDropdown(e, options);
  };

  const renderGroupName = (pig_group) => {
    if (isAdmin && pig_group?.id) {
      return (
        <Link to={`/barnsheets/groups/${pig_group.id}`} className="color-primary">
          {pig_group.name}
        </Link>
      );
    }
    return pig_group?.name || 'N/A';
  };

  const farmIds = new Set(events.map(({ farm_id }) => farm_id));
  const farmsData = [...farmIds].map((farmId) => {
    const findFarm = ({ farm_id }) => farm_id === farmId;
    const { farm } = events.find(findFarm);
    const farmEvents = events.filter(findFarm);
    return {
      farm,
      data: farmEvents.map(({ additional_data, pig_group }) => ({ additional_data, pig_group })),
    };
  }).filter(({ farm }) => isUserHasRoles(currentUser, farm));

  return (
    <ActivityBox
      className={className}
      isFlagged={flagged}
      isTruncated={isTruncated}
      hasMarker={!isChildActivity}
      isUnseen={!is_viewed}
      markAsSeen={markAsSeen}
      onViewFullActivity={onViewFullActivity}
    >
      {!isMinimized &&
        <LastCommentInfo activity={activity} currentUser={currentUser} />
      }
      <ActivityHeader
        activityId={id}
        onOpenDropdown={handleOpenDropdown}
        onDoubleClick={toggleMinimized}
      >
        <ActivityHeaderBadge isCentered>
          <FormattedMessage id="general.withdrawalWarning" tagName="b" />
        </ActivityHeaderBadge>
      </ActivityHeader>
      <ActivityBodyCollapse isClosed={isMinimized}>
        <div className={cn('warning-text')}>
          <FormattedMessage
            tagName="div"
            id="component.generalWithdrawalWarning.text"
            values={{
              b: (weight) => <b>{weight}</b>,
              orange: (msg) => <span className={cn('brand-orange')}>{msg}</span>,
              date: formatDateForUser(moment(activity.created_at).subtract(1, 'day'), countryCode),
            }}
          />
          <div className={cn('timestamp')}>
            {moment(created_at).format('MMM DD, YYYY, hh:mm a')}
          </div>
          <ActivityMentions isAdmin={isAdmin} users={mentions} />
        </div>
        {!!farmsData.length && (
          <div className="ph-30 mb-25">
            <Collapse
              className={cn('collapse')}
              title={<FormattedMessage id="general.button.showDetails" />}
            >
              <Divider />
              {farmsData.map(({ farm, data }, idx) => (
                <Collapse
                  key={idx}
                  className={cn('collapse', 'child')}
                  title={renderFarmTitle(farm)}
                >
                  <Divider />
                  <div className={cn('collapse-body')}>
                    {/* Column Headers */}
                    <div className={cn('row', 'header')}>
                      <div className={cn('column-header')}>
                        <FormattedMessage id="general.group" />
                      </div>
                      <div className={cn('column-header')}>
                        <FormattedMessage id="general.medication" />
                      </div>
                      <div className={cn('column-header')}>
                        <FormattedMessage id="general.estWeight" />
                      </div>
                      <div className={cn('column-header')}>
                        <FormattedMessage id="general.threshold" />
                      </div>
                      <div className={cn('column-header')}>
                        <FormattedMessage id="general.usClearance" />
                      </div>
                      <div className={cn('column-header')}>
                        <FormattedMessage id="general.exportClearance" />
                      </div>
                    </div>
                    {/* Data Rows - one row per treatment */}
                    {data.map(({ additional_data, pig_group }, pgIdx) => {
                      const clearMessage = intl.formatMessage({ id: 'general.clear' });
                      return additional_data.withdrawal_data?.map((treatment, treatmentIdx) => (
                        <div className={cn('row')} key={pgIdx + '-' + treatmentIdx}>
                          <div className={cn('group-name')}>
                            {renderGroupName(pig_group)}
                          </div>
                          <div className={cn('medication-name')}>
                            <Popover
                              content={<TreatmentFarmfeedTooltip treatmentId={treatment.id} isAdmin={isAdmin} />}
                              placement="bottomLeft"
                            >
                              <span title={treatment.name}>{renderMedicationName(treatment)}</span>
                            </Popover>
                          </div>
                          <div className={cn('estimated-weight')}>
                            <FormattedWeight value={additional_data.pg_estimated_weight} />
                          </div>
                          <div className={cn('threshold-date')}>
                            {renderThresholdDate(additional_data.threshold_date, intl)}
                          </div>
                          <div
                            className={cn('clearance-date', {
                              'clear': renderClearanceDate(treatment.us_clearance_date, intl) === clearMessage
                            })}
                          >
                            {renderClearanceDate(treatment.us_clearance_date, intl)}
                          </div>
                          <div
                            className={cn('clearance-date', {
                              'clear': renderClearanceDate(treatment.export_clearance_date, intl) === clearMessage
                            })}
                          >
                            {renderClearanceDate(treatment.export_clearance_date, intl)}
                          </div>
                        </div>
                      )) || [];
                    })}
                  </div>
                </Collapse>
              ))}
            </Collapse>
          </div>
        )}
        {!isChildActivity && (
          <>
            <ActivityActionsBar
              activity={activity}
              currentUser={currentUser}
              openDropdown={openDropdown}
              setActivityFlag={setActivityFlag}
            />
            <FarmFeedRowComments activity={activity} />
            <FarmFeedRowAddComment activity={activity} />
          </>
        )}
      </ActivityBodyCollapse>
    </ActivityBox>
  );
};

GeneralWithdrawalWarning.contextTypes = {
  router: T.object.isRequired
};

GeneralWithdrawalWarning.propTypes = {
  className: T.string,
  activity: T.object.isRequired,
  currentUser: T.object.isRequired,
  markAsSeen: T.func,
  isChildActivity: T.bool,
  openDropdown: T.func.isRequired,
  setActivityFlag: T.func.isRequired,
  // from hoc
  isTruncated: T.bool.isRequired,
  isMinimized: T.bool.isRequired,
  toggleMinimized: T.func.isRequired,
  onViewFullActivity: T.func.isRequired,
  defaultDropdownOptions: T.array.isRequired,
  countryCode: T.string,
};

GeneralWithdrawalWarning.defaultProps = {
  isChildActivity: false,
};

const ConnectedComponent = connect(
  (state) => ({
    countryCode: getCurrentCountryCode(state),
  })
)(GeneralWithdrawalWarning);

export default withActivityController(ConnectedComponent, { affectRerenderKeys });
